import { IconButton, Stack, TextField, Divider } from '@mui/material';
import type { PerUnitProduct } from '@vy/services';
import { isNotNullOrUndefined, removeInIndex } from '@vy/utils';
import React, { useCallback, useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { CloseIcon } from '../../../../../../../components/icons';
import { useFormErrorNotificationContext } from '../../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { getDefaultPerUnitUsageProductVariant } from '../../../../create-plan-page-consts/add-item-button-consts';
import { useChargeContext } from '../../../charge-context';
import type { NewPlanForm } from '../../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { PlanChargesContainer } from '../../../PlanChargesContainer';
import ProductDotMenuDropdown from '../../../ProductDotMenu';
import { AddPerUnitUsageChargeVariant } from '../PerUnitUsageCharge/per-unit-usage-charge-components/AddPerUnitUsageChargeVariant';
import { PerUnitUsageChargeEditBottomSection } from '../PerUnitUsageCharge/per-unit-usage-charge-components/PerUnitUsageChargeEditBottomSection';
import { UsageProductGroupDialogForm } from '../UsageProductGroupDialogForm';
import { PerUnitUsageChargeEditVariantsList } from './per-unit-usage-charge-edit-components/PerUnitUsageChargeEditVariantsList';
import { usePlanCreationFormUtils } from '../../../../../../../contexts/create-plan-context/CreatePlanProvider/hooks/use-plan-creation-form-utils';

export const PerUnitUsageChargeEdit: React.FC = () => {
  const { item: product, itemIndex: productIndex } = useChargeContext<PerUnitProduct>();
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();

  const {
    register,
    getValues,
    formState: { errors },
    setValue,
  } = useFormContext<PlanInputForm, NewPlanForm>();
  const [isProductGroupDialogOpen, setIsProductGroupDialogOpen] = useState(false);
  const { isFieldEditable } = usePlanCreationFormUtils();

  const productNameRegister = register(`usageProducts.${productIndex}.name`, {
    value: product.name,
  });

  registerErrorField(productNameRegister.name);

  const isGroupedProduct = product.variants.length > 1;

  const onDelete = useCallback(() => {
    const updatedProducts = removeInIndex(getValues('usageProducts'), productIndex);
    setValue('usageProducts', updatedProducts);
  }, [productIndex]);

  const addVariant = () => {
    const newVariant = getDefaultPerUnitUsageProductVariant();

    const updatedProductVariants = [...product.variants, newVariant];
    setValue(`usageProducts.${productIndex}.variants`, updatedProductVariants);
  };

  const handleConvertToGroup = () => {
    setIsProductGroupDialogOpen(true);
  };

  function handleGroupDelete() {
    const [firstVariant] = product.variants;
    setValue(`usageProducts.${productIndex}.name`, undefined);
    setValue(`usageProducts.${productIndex}.variants`, [firstVariant]);
  }

  return (
    <div
      className={twMerge(
        'flex flex-col gap-y-4',
        isFieldEditable(`usageProducts.${productIndex}`) ? '' : 'pointer-events-none opacity-40',
      )}
    >
      <PlanChargesContainer
        key={product.id}
        className="relative px-0 pb-3"
        id={`create-product-${productIndex}`}
      >
        {isGroupedProduct && (
          <div className="max-w-xs px-6 pt-8">
            <TextField
              placeholder="Text here"
              {...productNameRegister}
              label="Group Name"
              size="medium"
              defaultValue={getValues(productNameRegister.name) ?? ''}
              helperText={get(errors, productNameRegister.name)?.message}
              error={isNotNullOrUndefined(get(errors, productNameRegister.name))}
            />
          </div>
        )}
        <div>
          <div className="absolute right-3 top-3">
            <Stack
              direction="row"
              divider={<Divider orientation="vertical" flexItem variant="middle" />}
              sx={{
                columnGap: 1
              }}
            >
              <ProductDotMenuDropdown
                isGrouped={isGroupedProduct}
                onCreateGroup={handleConvertToGroup}
                onDeleteGroup={handleGroupDelete}
              />

              <IconButton
                onClick={onDelete}
                sx={{
                  width: '40px',
                  height: '40px',
                }}
              >
                <CloseIcon
                  color="#0D1B27DE"
                  style={{
                    transform: 'scale(0.7)',
                  }}
                />
              </IconButton>
            </Stack>
          </div>

          <UsageProductGroupDialogForm
            onCreateClick={() => !isGroupedProduct && addVariant()}
            open={isProductGroupDialogOpen}
            onClose={() => setIsProductGroupDialogOpen(false)}
            productIndex={productIndex}
          />

          <PerUnitUsageChargeEditVariantsList variants={product.variants} productIndex={productIndex} />

          {isGroupedProduct && (
            <div className="pt-2 px-6">
              <AddPerUnitUsageChargeVariant onAddVariant={() => addVariant()} />
            </div>
          )}

          <Divider className="mb-3 mt-2" />

          <div className="px-6">
            <PerUnitUsageChargeEditBottomSection {...{ product, productIndex }} />
          </div>
        </div>
      </PlanChargesContainer>
    </div>
  );
};
